// --- Imports ---
import { useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { parseISO } from 'date-fns';
import { InfoIcon, Loader2 } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { Textarea } from '@/components/ui/textarea';
import { formatMinutesToTime } from '@/helpers';
import { useModal } from '@/hooks/use-modal';
import {
  CustomRadioGroup,
  DateRangePicker,
  getAssignmentStatusOptions,
  TimePicker,
  usePermissions,
} from '@/modules/common';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { SingleSelectWithPagination } from '@/modules/common/components/molecules/selectors/single-select/single-select-with-pagination';
import { mapTeamMemberStatusToDTO } from '@/modules/common/mappers/team-member-status.mapper';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { RateSource } from '@/modules/common/types/rate-source';
import {
  DEFAULT_SCHEDULED_WORK,
  FIELD_CURRENCY,
  FIELD_DATE_RANGE,
  FIELD_DATE_RANGE_FROM,
  FIELD_DATE_RANGE_TO,
  FIELD_DO_SKIP_WEEKDAYS,
  FIELD_IS_BACKGROUND_WORK,
  FIELD_IS_BILLABLE,
  FIELD_NOTE,
  FIELD_PROBABILITY,
  FIELD_PROJECT_ID,
  FIELD_RATE,
  FIELD_RATE_SOURCE,
  FIELD_SCHEDULED_WORK,
  FIELD_STATUS,
  FIELD_USER_ID,
  PROJECTS_QUERY_KEY,
  TEAM_MEMBERS_QUERY_KEY,
} from '@/modules/planner/constants/assignment-modal-constants';
import {
  getDefaultAssignmentProbability,
  isAssignmentProbabilityVisible,
} from '@/modules/planner/helpers/assignment/probability';
import { getShortProjects } from '@/modules/projects/api/short-projects.api';
import { ProjectStatus } from '@/modules/projects/types/project';
import { getShortTeamMembers } from '@/modules/team/api/team.api';
import { formatDisplayDate, normalizeToStartOfDayUTC } from '@/utils/date-formatters';
import { compareFormValues } from '@/utils/form-state.utils';
import { logger } from '@/utils/logger/logger';

import { ASSIGNMENT_TEAM_MEMBER_FILTER_STATUSES } from '../../../constants';
import {
  renderProjectOption,
  renderProjectTrigger,
  renderUserOption,
  renderUserTrigger,
} from '../../../helpers';
import {
  useAssignmentBookedMinutesPreview,
  useAssignmentCloseConfirmation,
  useAssignmentDateState,
  useAssignmentFormDirty,
  useAssignmentProbabilityState,
  useAssignmentRateManagement,
  useAssignmentRateQueries,
  useAssignmentValidation,
  useCreateAssignment,
  useCurrencyOptions,
  useUpdateAssignment,
} from '../../../hooks';
import { useAssignmentDisplayState } from '../../../hooks/assignment-modal/use-assignment-display-state';
import { AssignmentStatus } from '../../../types/assignment-status';
import { assignmentFormSchema } from '../../../validations';
import { OverlapWarningModal } from '../overlap-warning-modal';
import { AssignmentModalRateFields } from './assignment-modal-rate-fields';
import { AssignmentProbabilityField } from './assignment-probability-field';
import { AssignmentTotalBookedField } from './assignment-total-booked-field';
import { buildCreatePayload, buildUpdatePayload } from './helpers/build-assignment-payload';

// --- Constants ---
const TAG = 'AssignmentModal:';

// --- Types ---
export interface AssignmentFormValues {
  id?: number;
  userId: string;
  projectId: string;
  scheduledWork: string;
  dateRange: {
    from: Date;
    to: Date;
  };
  status: AssignmentStatus;
  probability: number | null;
  rateSource?: RateSource;
  rate: string;
  currency: string;
  isBillable: boolean;
  doSkipWeekdays: boolean;
  isBackgroundWork: boolean;
  note?: string;
}

export interface AssignmentModalProps {
  open: boolean;
  onClose: () => void;
  initialData?: Partial<
    AssignmentFormValues & {
      scheduledWorkInMinutes: number;
      projectLabel?: string;
      projectColorHex?: string;
      projectProbability?: number | null;
      syncToken?: string;
      userName?: string;
      userProfileImageUrl?: string;
      projectBoundRateSource?: RateSource | null;
      bookedMinutes?: number;
    }
  >;
}

// --- Helper functions ---
function getDefaultValues({
  initialData,
}: {
  initialData: AssignmentModalProps['initialData'];
}): AssignmentFormValues {
  const status = initialData?.status ?? AssignmentStatus.Confirmed;
  const shouldPreserveMissingProbability = !!initialData?.id && initialData.probability == null;

  return {
    id: initialData?.id,
    userId: initialData?.userId ? String(initialData.userId) : '',
    projectId: initialData?.projectId ? String(initialData.projectId) : '',
    scheduledWork: initialData?.scheduledWorkInMinutes
      ? formatMinutesToTime(initialData.scheduledWorkInMinutes)
      : DEFAULT_SCHEDULED_WORK,
    dateRange: {
      from: initialData?.dateRange?.from ? initialData.dateRange.from : new Date(),
      to: initialData?.dateRange?.to ? initialData.dateRange.to : new Date(),
    },
    status,
    probability:
      initialData?.probability ??
      (!shouldPreserveMissingProbability && isAssignmentProbabilityVisible(status)
        ? getDefaultAssignmentProbability(initialData?.projectProbability)
        : null),
    // Only set rateSource if it's a valid enum value, otherwise leave undefined
    rateSource:
      initialData?.rateSource && Object.values(RateSource).includes(initialData.rateSource)
        ? initialData.rateSource
        : undefined,
    rate: initialData?.rate ?? '',
    currency: initialData?.currency ?? '',
    isBillable: initialData?.isBillable ?? true,
    doSkipWeekdays: initialData?.doSkipWeekdays ?? true,
    isBackgroundWork: initialData?.isBackgroundWork ?? false,
    note: initialData?.note ?? '',
  };
}

// --- Private sub-components ---

function FormInfoAlert({ children }: { children: React.ReactNode }) {
  return (
    <Alert variant="info">
      <AlertDescription className="flex items-start gap-1.5">
        <InfoIcon size={16} className="mt-0.5 shrink-0" /> {children}
      </AlertDescription>
    </Alert>
  );
}

// --- Main Component ---

export function AssignmentModal({ open, onClose, initialData = {} }: AssignmentModalProps) {
  const { t } = useTranslation(['authenticated.planner', 'common']);
  const [selectedProject, setSelectedProject] = useState<DropdownSearchItem | null>(null);
  const [selectedUser, setSelectedUser] = useState<DropdownSearchItem | null>(null);

  const {
    open: isOverlapWarningModalOpen,
    onClose: closeOverlapAssignmentModal,
    onOpen: openOverlapAssignmentModal,
  } = useModal();

  const defaultValues = getDefaultValues({ initialData });
  const isEditMode = !!initialData?.id;

  // Read-only mode: users with AssignmentsViewAll but not AssignmentsEdit can
  // open an existing assignment to inspect it, but every field is disabled and
  // the save button is hidden (OPS-1569). Create mode is never read-only.
  const { hasPermission } = usePermissions();
  const isReadOnly = isEditMode && !hasPermission(OpsiumPolicy.AssignmentsEdit);

  // --- Form setup ---
  const form = useForm<AssignmentFormValues>({
    resolver: zodResolver(assignmentFormSchema),
    defaultValues,
    mode: 'onChange',
  });
  const {
    clearErrors,
    formState,
    control,
    getValues,
    reset,
    resetField,
    setValue,
    trigger,
    watch,
  } = form;

  const userId = watch(FIELD_USER_ID);
  const projectId = watch(FIELD_PROJECT_ID);
  const dateRange = watch(FIELD_DATE_RANGE);
  const rateSource = watch(FIELD_RATE_SOURCE);
  const currentRate = watch(FIELD_RATE);
  const currentCurrency = watch(FIELD_CURRENCY);
  const isBillable = watch(FIELD_IS_BILLABLE);
  const status = watch(FIELD_STATUS);
  const probability = watch(FIELD_PROBABILITY);
  const scheduledWork = watch(FIELD_SCHEDULED_WORK);
  const doSkipWeekdays = watch(FIELD_DO_SKIP_WEEKDAYS);
  const isBackgroundWork = watch(FIELD_IS_BACKGROUND_WORK);

  const isRateDisabledByBillable = !isBillable;

  // --- Date state ---

  const {
    isDatePickerOpen,
    setIsDatePickerOpen,
    committedDateFrom,
    setCommittedDateFrom,
    committedDateTo,
    setCommittedDateTo,
    handleDatePickerOpenChange,
  } = useAssignmentDateState({
    initialDateFrom: initialData?.dateRange?.from,
    initialDateTo: initialData?.dateRange?.to,
    dateRangeFrom: dateRange?.from,
    dateRangeTo: dateRange?.to,
  });

  // Team members query key includes committed dates so the selector re-fetches when dates change
  const teamMembersMatchesPeriod = useMemo(
    () =>
      committedDateFrom && committedDateTo
        ? {
            startDate: normalizeToStartOfDayUTC(committedDateFrom).toISOString(),
            endDate: normalizeToStartOfDayUTC(committedDateTo).toISOString(),
          }
        : undefined,
    [committedDateFrom, committedDateTo]
  );

  const teamMembersQueryKey = useMemo(
    () => [
      TEAM_MEMBERS_QUERY_KEY,
      teamMembersMatchesPeriod?.startDate ?? '',
      teamMembersMatchesPeriod?.endDate ?? '',
    ],
    [teamMembersMatchesPeriod]
  );

  // --- Rates-on-project queries ---

  const {
    ratesData,
    isLoadingRates,
    rateOptions,
    isBeforeContractStart,
    shouldReuseContractStartData,
    hasRealRateOptions,
    canLoadRateOptions,
    onRateSourceDropdownOpen,
  } = useAssignmentRateQueries({
    open,
    userId,
    projectId,
    currentRate,
    committedDateFrom,
    committedDateTo,
    isEditMode,
    initialData,
  });

  // --- Rate management ---

  const { handleBillableChange, resetSavedRateValues } = useAssignmentRateManagement({
    watch,
    setValue,
    resetField,
    clearErrors,
    trigger,
    ratesData,
    rateOptions,
    isBeforeContractStart,
    initialAssignmentId: initialData?.id,
    isBillable,
    rateSource,
    currentRate,
    currentCurrency,
  });

  // --- Total booked hours ---

  const {
    bookedMinutes: previewBookedMinutes,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = useAssignmentBookedMinutesPreview({
    open,
    userId,
    dateFrom: committedDateFrom,
    dateTo: committedDateTo,
    scheduledWork,
    doSkipWeekdays,
    isBackgroundWork,
    assignmentId: initialData?.id,
    initial:
      initialData?.id && initialData.bookedMinutes !== undefined
        ? {
            values: {
              userId: defaultValues.userId,
              dateFrom: defaultValues.dateRange.from,
              dateTo: defaultValues.dateRange.to,
              scheduledWork: defaultValues.scheduledWork,
              doSkipWeekdays: defaultValues.doSkipWeekdays,
              isBackgroundWork: defaultValues.isBackgroundWork,
            },
            bookedMinutes: initialData.bookedMinutes,
          }
        : undefined,
  });

  const { currencyOptions, isLoadingCurrencies, handleCurrencyDropdownOpen } = useCurrencyOptions();
  const isFormDirty = useAssignmentFormDirty({ formState, watch });

  // --- Mutations ---

  const createMutation = useCreateAssignment({
    onSuccess: () => handleClose(),
  });

  const updateMutation = useUpdateAssignment({
    onSuccess: () => handleClose(),
  });

  const {
    showConfirmClose,
    handleRequestClose,
    handleConfirmClose,
    handleCancelClose,
    handleClose,
  } = useAssignmentCloseConfirmation({
    isFormDirty,
    isDatePickerOpen,
    setIsDatePickerOpen,
    onClose,
  });

  const { isPending: validatingOverlap, mutateAsync: validateOverlap } = useAssignmentValidation();

  // --- Effects ---

  // Initialise the form only on the rising edge of `open` (a real open), never on
  // subsequent re-renders while already open — re-running reset(defaultValues)
  // mid-session would wipe the user's input.
  const wasOpenRef = useRef(false);
  const wasInitialSyncedRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setSelectedProject(null);
      setSelectedUser(null);
      reset(defaultValues);
      resetSavedRateValues();
      setCommittedDateFrom(initialData?.dateRange?.from ? initialData.dateRange.from : new Date());
      setCommittedDateTo(initialData?.dateRange?.to ? initialData.dateRange.to : new Date());
    }
    wasOpenRef.current = open;
  }, [open, JSON.stringify(defaultValues)]);

  // Sync initialData into the form exactly once per open (guarded by a ref), not on
  // every re-render while open. Otherwise the optimistic cache update on submit
  // remounts this keyed form subtree and re-runs the effect, which in create mode
  // writes setValue(FIELD_PROJECT_ID, '') (initialData.projectId is undefined) —
  // wiping the user's project and collapsing the rate-source options mid-session.
  useEffect(() => {
    if (!open || wasInitialSyncedRef.current) return;
    wasInitialSyncedRef.current = true;
    setValue(FIELD_USER_ID, initialData?.userId ? String(initialData.userId) : '');
    setValue(FIELD_PROJECT_ID, initialData.projectId ? String(initialData.projectId) : '');
    setValue(
      FIELD_DATE_RANGE_FROM,
      initialData.dateRange?.from ? initialData.dateRange.from : new Date()
    );
    setValue(
      FIELD_DATE_RANGE_TO,
      initialData.dateRange?.to ? initialData.dateRange.to : new Date()
    );
  }, [
    open,
    initialData?.userId,
    initialData?.projectId,
    initialData.dateRange?.from,
    initialData.dateRange?.to,
  ]);

  // Reset the one-shot guard when the modal closes so the next open re-syncs.
  useEffect(() => {
    if (!open) wasInitialSyncedRef.current = false;
  }, [open]);

  const {
    currentProjectProbability,
    currentProjectStatus,
    shouldShowProbability,
    shouldShowProbabilityMismatchAlert,
  } = useAssignmentProbabilityState({
    open,
    isEditMode,
    initialData,
    projectId,
    selectedProject,
    status,
    probability,
    getValues,
    setValue,
  });

  useEffect(() => {
    if (!open || isReadOnly || !shouldShowProbability || probability !== null) return;

    void trigger(FIELD_PROBABILITY);
  }, [isReadOnly, open, probability, shouldShowProbability, trigger]);

  // --- Display state ---

  const {
    initialUserItem,
    initialProjectItem,
    initialCurrencyItem,
    shouldShowProjectStatusAlert,
    shouldShowRateSourceAlert,
    isInitialRateNonProjectBased,
    effectiveDateLabel,
  } = useAssignmentDisplayState({
    initialData,
    rateSource,
    currentCurrency,
    isBillable,
    committedDateFrom,
    committedDateTo,
    shouldReuseContractStartData,
    projectStatus: currentProjectStatus,
    status,
  });

  const handleProjectSelect = (project: DropdownSearchItem | null) => {
    setSelectedProject(project);

    if (project?.status !== ProjectStatus.Tentative) return;
    if (isEditMode && project.id === String(initialData?.projectId)) return;

    setValue(FIELD_STATUS, AssignmentStatus.Unconfirmed, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  // --- Submit handler ---

  const handleSubmit: SubmitHandler<AssignmentFormValues> = async values => {
    try {
      const isDirty = compareFormValues(
        { id: formState.defaultValues?.id, ...values },
        formState.defaultValues
      );

      if (!isDirty) return;

      const validationResponse = await validateOverlap({
        userId: userId ? Number(userId) : 0,
        projectId: projectId ? Number(projectId) : 0,
        startDate: dateRange?.from ? normalizeToStartOfDayUTC(dateRange.from).toISOString() : '',
        endDate: dateRange?.to ? normalizeToStartOfDayUTC(dateRange.to).toISOString() : '',
        assignmentId: initialData?.id ?? undefined,
      });

      if (validationResponse.overlapping) {
        return openOverlapAssignmentModal();
      }

      const payloadContext = {
        values,
        initialData,
        selectedUserName: selectedUser?.name,
        selectedProjectName: selectedProject?.name,
        selectedProjectColorHex: selectedProject?.colorHex,
      };

      if (initialData?.id) {
        updateMutation.mutate(buildUpdatePayload(payloadContext));
      } else {
        createMutation.mutate(buildCreatePayload(payloadContext));
      }
    } catch (err) {
      logger.error(TAG, 'Failed to submit assignment', err);
    }
  };

  // --- Render ---

  const isLoading = createMutation.isPending || updateMutation.isPending || validatingOverlap;

  return (
    <>
      <Dialog open={open} onOpenChange={handleRequestClose}>
        <DialogContent className="flex max-h-[90dvh] max-w-xl flex-col">
          <DialogHeader>
            <DialogTitle className="pl-2 pr-2">
              {isReadOnly
                ? t('viewAssignment')
                : initialData.id
                  ? t('editAssignment')
                  : t('createAssignment')}
            </DialogTitle>
            <DialogDescription className="pl-2 pr-2">{t('provideDetails')}</DialogDescription>
          </DialogHeader>

          {/* Render the form body only while open. The parent clears initialData on
              close, so unmounting the body avoids re-rendering the form against the
              now-empty data while the dialog animates out. */}
          {open && (
            <Form {...form} key={JSON.stringify(defaultValues)}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="flex min-h-0 flex-col">
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pb-2 pl-2 pr-2">
                  {/* User Selection */}
                  <SingleSelectWithPagination
                    name={FIELD_USER_ID}
                    control={control}
                    label={t('form.userId.label')}
                    placeholder={t('form.userId.placeholder')}
                    isClearSelectionEnabled={false}
                    queryKey={teamMembersQueryKey}
                    loadItems={cursor =>
                      getShortTeamMembers({
                        filter: {
                          statuses:
                            ASSIGNMENT_TEAM_MEMBER_FILTER_STATUSES.map(mapTeamMemberStatusToDTO),
                          matchesWholePeriod: teamMembersMatchesPeriod,
                        },
                        pagination: { cursor },
                      })
                    }
                    searchItems={(searchTerm, cursor) =>
                      getShortTeamMembers({
                        filter: {
                          searchString: searchTerm,
                          statuses:
                            ASSIGNMENT_TEAM_MEMBER_FILTER_STATUSES.map(mapTeamMemberStatusToDTO),
                          matchesWholePeriod: teamMembersMatchesPeriod,
                        },
                        pagination: { cursor },
                      })
                    }
                    disabled={!!initialData?.id}
                    renderOption={renderUserOption}
                    renderTrigger={renderUserTrigger}
                    initialSelectedItem={initialUserItem}
                    onSelect={setSelectedUser}
                    additionalItems={selectedUser ? [selectedUser] : undefined}
                  />

                  {/* Project & Effort */}
                  <div className="grid grid-cols-2 gap-4">
                    <SingleSelectWithPagination
                      name={FIELD_PROJECT_ID}
                      control={control}
                      label={t('form.projectId.label')}
                      placeholder={t('form.projectId.placeholder')}
                      queryKey={PROJECTS_QUERY_KEY}
                      loadItems={cursor => getShortProjects({ pagination: { cursor } })}
                      searchItems={(searchTerm, cursor) =>
                        getShortProjects({
                          filter: { searchString: searchTerm },
                          pagination: { cursor },
                        })
                      }
                      renderOption={renderProjectOption}
                      renderTrigger={renderProjectTrigger}
                      initialSelectedItem={initialProjectItem}
                      onSelect={handleProjectSelect}
                      isClearSelectionEnabled={false}
                      disabled={isReadOnly}
                    />
                    <div className="flex min-w-0 gap-4">
                      <div className="min-w-0 flex-1">
                        <TimePicker
                          name={FIELD_SCHEDULED_WORK}
                          control={control}
                          label={t('form.scheduledWork.label')}
                          disabled={isReadOnly}
                        />
                      </div>
                      <AssignmentTotalBookedField
                        bookedMinutes={previewBookedMinutes}
                        isLoading={isPreviewLoading}
                        isError={isPreviewError}
                        doSkipWeekdays={doSkipWeekdays}
                        isBackgroundWork={isBackgroundWork}
                      />
                    </div>
                  </div>

                  {/* Date Range */}
                  <DateRangePicker
                    name={FIELD_DATE_RANGE}
                    control={control}
                    label={t('form.dateRange.label')}
                    open={isDatePickerOpen}
                    onOpenChange={handleDatePickerOpenChange}
                    disabled={isReadOnly}
                  />

                  {/* Start date alert */}
                  {!isDatePickerOpen && isBeforeContractStart && (
                    <Alert variant="warning">
                      <AlertDescription className="flex items-center gap-1.5">
                        <InfoIcon size={20} className="shrink-0" />{' '}
                        {t('form.dateRange.contractStartWarning', {
                          date: ratesData?.nearestEffectiveStartDate
                            ? formatDisplayDate(parseISO(ratesData.nearestEffectiveStartDate))
                            : '',
                        })}
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Status & Probability */}
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                    <div className="min-w-0 flex-1">
                      <CustomRadioGroup
                        control={control}
                        name={FIELD_STATUS}
                        disabled={isReadOnly || initialData.status === AssignmentStatus.Confirmed}
                        label={t('form.status.label')}
                        options={getAssignmentStatusOptions([
                          AssignmentStatus.Confirmed,
                          AssignmentStatus.Unconfirmed,
                          AssignmentStatus.Draft,
                        ])}
                      />
                    </div>
                    {shouldShowProbability && (
                      <div className="sm:w-[8.5rem]">
                        <AssignmentProbabilityField control={control} isReadOnly={isReadOnly} />
                      </div>
                    )}
                  </div>

                  {shouldShowProjectStatusAlert && (
                    <FormInfoAlert>
                      {t('form.status.tentativeConfirmedRecommendation')}
                    </FormInfoAlert>
                  )}

                  {shouldShowProbabilityMismatchAlert && (
                    <FormInfoAlert>
                      {t('form.probability.projectMismatch', {
                        probability: currentProjectProbability,
                      })}
                    </FormInfoAlert>
                  )}

                  {/* Rate Source, Rate & Currency. Always rendered: rate visibility is decided by
                      the data, not by the UI. The backend masks values the user may not see and
                      leaves the rate source, currency and billability intact, so a user without
                      PlannerFinancialsView still sees which kind of billing applies and can pick a
                      rate source or enter a custom rate. */}
                  <AssignmentModalRateFields
                    control={control}
                    rateOptions={rateOptions}
                    currencyOptions={currencyOptions}
                    isLoadingCurrencies={isLoadingCurrencies}
                    isLoadingRates={isLoadingRates}
                    initialCurrencyItem={initialCurrencyItem}
                    isRateDisabledByBillable={isRateDisabledByBillable}
                    isRateSourceSelectable={
                      hasRealRateOptions || rateOptions.length > 1 || canLoadRateOptions
                    }
                    onRateSourceDropdownOpen={onRateSourceDropdownOpen}
                    rateSource={rateSource}
                    shouldShowRateSourceAlert={shouldShowRateSourceAlert}
                    isInitialRateNonProjectBased={isInitialRateNonProjectBased}
                    effectiveDateLabel={effectiveDateLabel}
                    onCurrencyDropdownOpen={handleCurrencyDropdownOpen}
                    isReadOnly={isReadOnly}
                  />

                  {/* Billable, Skip Weekends & Background Work */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_2fr_2fr]">
                    <FormField
                      control={control}
                      name={FIELD_IS_BILLABLE}
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-2">
                          <FormControl>
                            <Checkbox
                              checked={!!field.value}
                              disabled={isLoadingRates || isReadOnly}
                              onCheckedChange={checked => handleBillableChange(!!checked)}
                            />
                          </FormControl>
                          <FormLabel className="!mt-0 leading-none">
                            {t('form.isBillable.label')}
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={control}
                      name={FIELD_DO_SKIP_WEEKDAYS}
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-2">
                          <FormControl>
                            <Checkbox
                              checked={!!field.value}
                              disabled={isReadOnly}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="!mt-0 flex items-center gap-1 leading-none">
                            {t('form.doSkipWeekends.label')}
                            <SimpleTooltip
                              content={t('form.doSkipWeekends.tooltip')}
                              contentClassName="max-w-56"
                            >
                              <InfoIcon size={14} className="shrink-0 text-muted-foreground" />
                            </SimpleTooltip>
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={control}
                      name={FIELD_IS_BACKGROUND_WORK}
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-2">
                          <FormControl>
                            <Checkbox
                              checked={!!field.value}
                              disabled={isReadOnly}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="!mt-0 flex items-center gap-1 leading-none">
                            {t('form.isBackgroundWork.label')}
                            <SimpleTooltip
                              content={t('form.isBackgroundWork.tooltip')}
                              contentClassName="max-w-56"
                            >
                              <InfoIcon size={14} className="shrink-0 text-muted-foreground" />
                            </SimpleTooltip>
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Notes */}
                  <FormField
                    control={control}
                    name={FIELD_NOTE}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('form.notes.label')}</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={t('form.notes.placeholder')}
                            className="resize-y"
                            {...field}
                            value={field.value ?? ''}
                            disabled={isReadOnly}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Footer */}
                <DialogFooter
                  className={`mt-2 flex border-t pl-2 pr-2 pt-4 ${
                    isReadOnly ? 'justify-end' : 'justify-between'
                  }`}
                >
                  <Button type="button" variant="outline" onClick={handleRequestClose}>
                    {isReadOnly ? t('common:close') : t('common:cancel')}
                  </Button>
                  {/* Hide the submit button entirely for read-only (view-only) users (OPS-1569) */}
                  {!isReadOnly && (
                    <Button
                      type="submit"
                      disabled={
                        isLoading || !formState.isValid || (!!initialData.id && !isFormDirty)
                      }
                    >
                      {isLoading && <Loader2 className="animate-spin" />}
                      {initialData.id ? t('editAssignment') : t('createAssignment')}
                    </Button>
                  )}
                </DialogFooter>
              </form>
            </Form>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmClose} onOpenChange={handleCancelClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('discardChangesTitle')}</DialogTitle>
          </DialogHeader>
          <DialogDescription>{t('discardChangesDescription')}</DialogDescription>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelClose}>
              {t('discardChangesCancel')}
            </Button>
            <Button variant="destructive" onClick={handleConfirmClose}>
              {t('discardChangesConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OverlapWarningModal open={isOverlapWarningModalOpen} onClose={closeOverlapAssignmentModal} />
    </>
  );
}
